/**
 * columnDefinitions.js - Column schema helpers
 *
 * Provides utilities for creating and working with standardized column definitions
 * that include filter and sort metadata.
 */

/**
 * Creates a standard column definition with filter metadata
 * @param {Object} config - Column configuration
 * @param {string} config.path - Data property path for this column
 * @param {string} config.key - Unique key (defaults to path)
 * @param {string|function} config.label - Column header label or render function
 * @param {function} config.content - Custom cell render function (receives row data)
 * @param {boolean} config.filterable - Whether column shows as dropdown filter (default: false)
 * @param {boolean} config.searchable - Whether column can be text searched (default: false)
 * @param {string} config.filterType - Filter type: "exact", "contains", "startsWith", "custom"
 * @param {string} config.filterKey - Override key for filter state (defaults to path || key)
 * @param {string} config.filterAllValue - Value that means "no filter" (default: "All")
 * @param {boolean} config.useCustomOptions - Use custom options prop instead of auto-generating (default: false)
 * @param {function} config.filterFn - Custom filter function for type: "custom"
 * @param {function} config.filterOptionsTransform - Transform function for option values
 * @param {function} config.filterOptionsSortFn - Custom sort for filter options
 * @param {boolean} config.sortable - Whether column can be sorted (default: true)
 * @param {function} config.sortFn - Custom sort function
 * @param {string} config.width - Column width
 * @param {string} config.className - Cell CSS class
 * @param {string} config.headerClassName - Header cell CSS class
 * @returns {Object} Complete column definition
 */
export function createColumn(config) {
  const {
    path,
    key,
    label,
    content,
    // Filter configuration
    filterable = false, // Shows as dropdown filter
    searchable = false, // Can be text searched
    filterType = "exact",
    filterKey,
    filterAllValue = "All",
    useCustomOptions = false, // Use custom options prop instead of auto-generating
    filterFn,
    filterOptionsTransform,
    filterOptionsSortFn,
    // Sort configuration
    sortable = true,
    sortFn,
    // Display configuration
    width,
    className,
    headerClassName,
  } = config;

  return {
    // Core properties (existing schema)
    path,
    key: key || path,
    label,
    content,
    // Filter metadata
    filterable, // Shows as dropdown filter with auto-generated options
    searchable, // Can be text searched via search bar
    filterType,
    filterKey: filterKey || path || key,
    filterAllValue,
    useCustomOptions, // Use custom options prop instead of auto-generating
    filterFn,
    filterOptionsTransform,
    filterOptionsSortFn,
    // Sort metadata
    sortable,
    sortFn,
    // Display metadata
    width,
    className,
    headerClassName,
  };
}

/**
 * Extracts columns that are filterable from column definitions
 * @param {Array} columns - Array of column definitions
 * @returns {Array} Filterable column definitions
 */
export function getFilterableColumns(columns) {
  if (!columns || !Array.isArray(columns)) return [];
  return columns.filter((col) => col.filterable);
}

/**
 * Extracts columns that support text search from column definitions
 * @param {Array} columns - Array of column definitions
 * @returns {Array} Searchable column definitions (marked as searchable or contains/startsWith filter type)
 */
export function getSearchableColumns(columns) {
  if (!columns || !Array.isArray(columns)) return [];
  return columns.filter(
    (col) =>
      col.searchable ||
      (col.filterable &&
        (col.filterType === "contains" || col.filterType === "startsWith"))
  );
}

/**
 * Extracts columns that are sortable from column definitions
 * @param {Array} columns - Array of column definitions
 * @returns {Array} Sortable column definitions
 */
export function getSortableColumns(columns) {
  if (!columns || !Array.isArray(columns)) return [];
  return columns.filter((col) => col.sortable !== false);
}

/**
 * Finds a column by path or key
 * @param {Array} columns - Array of column definitions
 * @param {string} pathOrKey - Path or key to find
 * @returns {Object|undefined} Column definition or undefined
 */
export function findColumn(columns, pathOrKey) {
  if (!columns || !Array.isArray(columns) || !pathOrKey) return undefined;
  return columns.find((col) => col.path === pathOrKey || col.key === pathOrKey);
}

/**
 * Gets the filter key for a column (used to map to filter state)
 * @param {Object} column - Column definition
 * @returns {string} The filter key
 */
export function getColumnFilterKey(column) {
  if (!column) return undefined;
  return column.filterKey || column.path || column.key;
}

/**
 * Validates that a column definition has required properties
 * @param {Object} column - Column definition to validate
 * @returns {Object} { valid: boolean, errors: string[] }
 */
export function validateColumn(column) {
  const errors = [];

  if (!column) {
    return { valid: false, errors: ["Column is null or undefined"] };
  }

  if (!column.path && !column.key) {
    errors.push("Column must have either 'path' or 'key' property");
  }

  if (!column.label && typeof column.label !== "function") {
    errors.push("Column must have a 'label' property");
  }

  if (column.filterable && column.filterType === "custom" && !column.filterFn) {
    errors.push("Custom filter type requires 'filterFn' function");
  }

  return { valid: errors.length === 0, errors };
}
